import { DiscountModels } from '@getgreenline/products'
import { observable, action, computed } from 'mobx'

export class DiscountFilter implements DiscountModels.IDiscountFilter {
  @observable
  locationIds?: number[]

  @observable
  isAutomatic?: boolean

  @observable
  id?: number

  @observable
  name?: string

  @observable
  isLoyalty?: boolean

  @observable
  productIds?: string[]

  @observable
  categoryIds?: string[]

  @observable
  searchTerm = ''

  @observable
  salesChannelIds?: number[] | undefined

  constructor(discountFilter?: DiscountModels.IDiscountFilter) {
    if (discountFilter) {
      this.setFilter(discountFilter)
    }
  }

  @action
  setFilter = (discountFilter: DiscountModels.IDiscountFilter) => {
    this.locationIds = discountFilter.locationIds
    this.isAutomatic = discountFilter.isAutomatic
    this.id = discountFilter.id
    this.name = discountFilter.name
    this.isLoyalty = discountFilter.isLoyalty
    this.productIds = discountFilter.productIds
    this.categoryIds = discountFilter.categoryIds
    this.salesChannelIds = discountFilter.salesChannelIds
  }

  @computed
  get networkObject(): DiscountModels.IDiscountFilter {
    return {
      locationIds: this.locationIds,
      isAutomatic: this.isAutomatic,
      id: this.id,
      name: this.name,
      isLoyalty: this.isLoyalty,
      productIds: this.productIds,
      categoryIds: this.categoryIds,
      salesChannelIds: this.salesChannelIds,
    }
  }

  @computed
  get isFilterEmpty(): boolean {
    return !(
      this.searchTerm ||
      this.isLoyalty ||
      this.isAutomatic ||
      this.productIds ||
      this.categoryIds ||
      this.locationIds ||
      this.salesChannelIds
    )
  }

  @action
  addLocationId = (locationId: number) => {
    if (!this.locationIds) {
      this.locationIds = []
    }
    this.locationIds.push(locationId)
  }

  @action
  removeLocationId = (locationId: number) => {
    if (this.locationIds) {
      const newLocationIds = this.locationIds.filter((id: number) => id !== locationId)
      this.locationIds = newLocationIds.length > 0 ? newLocationIds : undefined
    }
  }

  @action
  setLocationIds = (locationIds?: number[]) => {
    this.locationIds = locationIds
  }

  @action
  setIsAutomatic = (isAutomatic?: boolean) => {
    this.isAutomatic = isAutomatic
  }

  @action
  setSearchTerm = (searchTerm: string) => {
    this.searchTerm = searchTerm

    if (searchTerm === '') {
      this.setName(undefined)
      this.setId(undefined)
      return
    }

    this.setName(searchTerm)
    const searchingId = isNaN(parseInt(searchTerm)) ? undefined : parseInt(searchTerm)
    this.setId(searchingId)
  }

  @action
  setId = (id?: number) => {
    this.id = id
  }

  @action
  setName = (name?: string) => {
    this.name = name
  }

  @action
  setIsLoyalty = (isLoyalty?: boolean) => {
    this.isLoyalty = isLoyalty
  }

  @action
  addProductId = (productId: string) => {
    if (!this.productIds) {
      this.productIds = []
    }
    this.productIds.push(productId)
  }

  @action
  removeProductId = (productId: string) => {
    if (this.productIds) {
      const newProductIds = this.productIds.filter((id: string) => id !== productId)
      this.productIds = newProductIds.length > 0 ? newProductIds : undefined
    }
  }

  @action
  setProductIds = (productIds?: string[]) => {
    this.productIds = productIds
  }

  @action
  addCategoryId = (categoryId: string) => {
    if (!this.categoryIds) {
      this.categoryIds = []
    }
    this.categoryIds.push(categoryId)
  }

  @action
  removeCategoryId = (categoryId: string) => {
    if (this.categoryIds) {
      const newCategoryIds = this.categoryIds.filter((id: string) => id !== categoryId)
      this.categoryIds = newCategoryIds.length > 0 ? newCategoryIds : undefined
    }
  }

  @action
  setCategoryIds = (categoryIds?: string[]) => {
    this.categoryIds = categoryIds
  }

  @action addSalesChannel = (salesChannelId: number) => {
    if (!this.salesChannelIds) this.salesChannelIds = []
    this.salesChannelIds.push(salesChannelId)
  }

  @action removeSalesChannel = (salesChannelId: number) => {
    if (!this.salesChannelIds) return

    const newSalesChannels = this.salesChannelIds.filter((id: number) => id !== salesChannelId)

    this.salesChannelIds = newSalesChannels.length > 0 ? newSalesChannels : undefined
  }

  @action toggleSalesChannel = (id: number, toggleActive: boolean) =>
    toggleActive ? this.addSalesChannel(id) : this.removeSalesChannel(id)

  @action
  reset = () => {
    this.id = undefined
    this.name = undefined
    this.isLoyalty = undefined
    this.isAutomatic = undefined
    this.locationIds = undefined
    this.productIds = undefined
    this.categoryIds = undefined
    this.searchTerm = ''
    this.salesChannelIds = undefined
  }
}
